// Equity Tycoon - Core type contracts
export type MarketPhase = 'BOOM' | 'STAGNATION' | 'CRISIS';

export interface ShareholderRecord {
  [playerId: string]: number;
}

export type District =
  | 'DISTRICT_A' // Old Town
  | 'DISTRICT_B' // Financial Harbor
  | 'DISTRICT_C' // Innovation Valley
  | 'DISTRICT_D' // Metropolis Center
  | 'DISTRICT_E' // Expansion Belt — balanced secondary cluster
  | 'DISTRICT_F' // Outer Rim — balanced third cluster
  | 'TRANSIT'
  | 'FINANCIAL'
  | 'SPECIAL';

/**
 * Colour-group balancing corridor: every commercial district must hold between
 * MIN_DISTRICT_TILES and MAX_DISTRICT_TILES properties on the live board.
 */
export const MIN_DISTRICT_TILES = 3;
export const MAX_DISTRICT_TILES = 5;
/** Canonical order of the balanced commercial clusters (core first, then annexes). */
export const DISTRICT_GROUPS: District[] = [
  'DISTRICT_A',
  'DISTRICT_B',
  'DISTRICT_C',
  'DISTRICT_D',
  'DISTRICT_E',
  'DISTRICT_F',
];

export type TileType = 'COMMERCIAL' | 'TRANSIT' | 'FINANCIAL' | 'SPECIAL';

export interface PropertyTile {
  id: string;
  name: string;
  district: District;
  tileType: TileType;
  baseCost: number;
  baseRent: number;
  upgradeLevel: number; // 0 = Vacant Lot, 1..5 = Buildings
  totalShares: number; // starts at 100, grows with rights issues
  shares: ShareholderRecord;
  upgradeCost: number;
  /** Live market valuation (drifts every round). Falls back to baseCost for legacy saves. */
  valuation: number;
  /** Round-over-round % change of valuation (for delta tags). */
  valuationDeltaPct: number;
  /** Zoning freeze: no rent collection and no upgrades. */
  isFrozen: boolean;
  isExpansionTile?: boolean;
  /**
   * Independent live rent yield (10 %–25 % of valuation). Decoupled from the tier
   * ladder: it drifts on every macro-cycle tick so identical tiers can pay
   * differently. Falls back to `yieldRateOf(upgradeLevel)` for legacy saves.
   */
  rentYieldRate?: number;
  /** Base rent at the current valuation & live yield (before the cycle multiplier). */
  rent?: number;
}

/** Rent yield ladder: 10% (Vacant) → 25% (Megastructure) of live valuation. */
export const YIELD_RATES = [0.1, 0.13, 0.16, 0.19, 0.22, 0.25];

/** Neutral opening point of the independent rent-yield corridor (10 %–25 %). */
export const DEFAULT_RENT_YIELD = 0.15;
/** Hard corridor for the drifting rent yield. */
export const MIN_RENT_YIELD = 0.1;
export const MAX_RENT_YIELD = 0.25;

/**
 * District monopoly is measured across the WHOLE colour group: a player who
 * holds more than 50 % of all issued shares in the district controls it, even
 * if they do not hold a majority on every individual street.
 */
export const MONOPOLY_SHARE_THRESHOLD = 0.5;
/** Rent uplift on every tile of a monopolised district. */
export const MONOPOLY_RENT_BONUS = 1.25;
/** Build-cost discount on upgrades inside a monopolised district. */
export const MONOPOLY_UPGRADE_DISCOUNT = 0.25;
/** One free upgrade per round for a district monopolist. */
export const MONOPOLY_FREE_UPGRADES_PER_ROUND = 1;

export const yieldRateOf = (upgradeLevel: number): number =>
  YIELD_RATES[Math.min(5, Math.max(0, upgradeLevel))] ?? 0.1;

// ---- Corporate finance: structured loans + treasury investments ----

/** Repayment structures offered by the Financial Desk. */
export type LoanStructure = 'ANNUITY' | 'INTEREST_ONLY' | 'BALLOON';

/** Selectable terms in rounds (Financial Desk). */
export const LOAN_TERMS = [3, 5, 8] as const;

/** Market-linked base rate per round: cheap credit in a boom, brutal in a crisis. */
export const LOAN_BASE_RATE: Record<MarketPhase, number> = {
  BOOM: 0.04,
  STAGNATION: 0.07,
  CRISIS: 0.12,
};

/** Balloon / zero-coupon loans carry an extra +2 % risk premium per round. */
export const BALLOON_RISK_PREMIUM = 0.02;
/** Flat fee on an early payoff of an active loan. */
export const PREPAYMENT_FEE = 0.025;

/** Treasury asset tiers available in the Investments tab. */
export type InvestmentTier = 'SAVINGS' | 'REIT' | 'VENTURE';

/** Exit penalty applied when a fund is cashed out before its horizon. */
export const INVESTMENT_EXIT_PENALTY = 0.03;

export interface Loan {
  id: string;
  playerId: string;
  principal: number;
  /** Nominal interest per round locked in at origination. */
  ratePerRound: number;
  termRounds: number;
  structure: LoanStructure;
  startedRound: number;
  maturityRound: number;
  roundsRemaining: number;
  /** Principal still owed (reduced by the principal share of each payment). */
  outstanding: number;
  /** Fixed payment per round (0 for balloon / zero-coupon loans). */
  perRoundPayment: number;
  /** Quoted overpayment over the full term. */
  totalInterest: number;
  interestPaid: number;
}

export interface Investment {
  playerId: string;
  tier: InvestmentTier;
  /** Net deposits (cost basis). */
  principal: number;
  /** Current mark-to-market value. */
  value: number;
  openedRound: number;
  /** Yield credited in the last processed round (0–1). */
  lastYieldRate: number;
}

/** Human-readable label per loan structure (used by the desk + ledger). */
export const LOAN_STRUCTURE_LABEL: Record<LoanStructure, { label: string; blurb: string }> = {
  ANNUITY: { label: 'Annuity · Anuitní splátka', blurb: 'Fixed principal + interest every round — lowest total overpayment.' },
  INTEREST_ONLY: { label: 'Interest-only · Úrokové splácení', blurb: 'Pay only interest each round; the whole principal falls due at maturity.' },
  BALLOON: { label: 'Balloon · Balónová splátka', blurb: 'Pay $0 per round; principal + compounded interest (+2 % premium) at maturity.' },
};

/** Metadata for the three treasury funds. */
export const INVESTMENT_TIER_META: Record<
  InvestmentTier,
  { name: string; label: string; avgYield: number; volatility: string; liquidity: string; horizonRounds: number; accent: string }
> = {
  SAVINGS: {
    name: 'Money Market',
    label: 'Konzervativní fond',
    avgYield: 0.03,
    volatility: '0 % · risk-free',
    liquidity: 'Instant withdrawal — no fees',
    horizonRounds: 0,
    accent: '#38bdf8',
  },
  REIT: {
    name: 'Real Estate Index',
    label: 'Vyvážený fond (REITs)',
    avgYield: 0.05,
    volatility: '+1 % … +10 %/round',
    liquidity: '3-round horizon · 3 % exit penalty',
    horizonRounds: 3,
    accent: '#34d399',
  },
  VENTURE: {
    name: 'Venture Growth',
    label: 'Dynamický / Rizikový fond',
    avgYield: 0.1,
    volatility: '−10 % (Crisis) … +22 % (Boom)',
    liquidity: 'Liquid · high drawdown risk',
    horizonRounds: 0,
    accent: '#a855f7',
  },
};

// ---- Multi-tier rent insurance (Financial Desk · Insurance tab) ----

/** Insurance tiers offered by the Financial Desk. */
export type InsurancePolicy = 'BASIC' | 'STANDARD' | 'VIP';

/** Premium (share of net worth), duration in rounds and covered share of rent. */
export const INSURANCE_POLICY_META: Record<
  InsurancePolicy,
  {
    name: string;
    label: string;
    /** Premium as a share of net worth (0–1). */
    premiumPct: number;
    /** Duration in rounds. */
    turns: number;
    /** Share of the rent liability covered while active (0–1). */
    coverage: number;
    /** VIP tier also blocks hostile takeovers of the policyholder's shares. */
    takeoverImmunity: boolean;
    accent: string;
    blurb: string;
  }
> = {
  BASIC: {
    name: 'Basic Policy',
    label: 'Základní spoluúčast',
    premiumPct: 0.02,
    turns: 3,
    coverage: 0.5,
    takeoverImmunity: false,
    accent: '#38bdf8',
    blurb: 'Ideální pro rychlý průchod kolem levnějších parcel.',
  },
  STANDARD: {
    name: 'Standard Umbrella',
    label: 'Komplexní ochrana',
    premiumPct: 0.05,
    turns: 4,
    coverage: 0.75,
    takeoverImmunity: false,
    accent: '#34d399',
    blurb: 'Klasická volba pro bezpečný pohyb po desce.',
  },
  VIP: {
    name: 'Executive Shield',
    label: 'Prémiový VIP štít',
    premiumPct: 0.08,
    turns: 6,
    coverage: 0.9,
    takeoverImmunity: true,
    accent: '#fbbf24',
    blurb: 'Maximální ochrana před agresivními boty.',
  },
};


export type AIArchetype = 'AGGRESSIVE' | 'CONSERVATIVE' | 'OPPORTUNISTIC';

/** Archetype as configured in the lobby — RANDOM is resolved to a concrete archetype on Start Game. */
export type AIArchetypeChoice = AIArchetype | 'RANDOM';

/** Victory condition selected in the pre-game lobby. */
export type VictoryCondition = 'NET_WORTH_100K' | 'NET_WORTH_150K' | 'LAST_STANDING' | 'ENDLESS';

/** A configurable seat in the pre-game setup lobby (2–10 players, humans and/or bots). */
export interface SetupPlayer {
  id: string;
  name: string;
  isAI: boolean;
  archetype: AIArchetypeChoice;
  color: string;
}

/** Full pre-game configuration captured by the setup lobby. */
export interface LobbyConfig {
  players: SetupPlayer[];
  startingCash: number;
  victory: VictoryCondition;
}

export const LOBBY_COLORS: string[] = [
  '#3b82f6', // Blue
  '#10b981', // Emerald
  '#f59e0b', // Amber
  '#f43f5e', // Rose
  '#a855f7', // Purple
  '#06b6d4', // Cyan
  '#f97316', // Orange
  '#6366f1', // Indigo
  '#84cc16', // Lime
  '#d946ef', // Fuchsia
];


export interface Player {
  id: string;
  name: string;
  isAI: boolean;
  aiArchetype?: AIArchetype;
  cash: number;
  debt: number;
  currentTileIndex: number;
  color: string;
  isBankrupt: boolean;
  /** Round in which this player already used their free monopoly upgrade (one per round). */
  lastFreeUpgradeRound?: number;
  /** Multi-tier rent insurance: rounds remaining while the policy is active (0 = uninsured). */
  insuranceTurnsRemaining: number;
  /** Share of the rent liability covered by the active policy (0–1; 0 = uninsured). */
  insuranceCoverageRate: number;
  /** Currently active insurance tier (null = uninsured). */
  insurancePolicy: InsurancePolicy | null;
  /** Creditworthiness 0–100. Drops hard on a distressed refinancing and drives loan pricing. */
  creditScore: number;
  /** Lifetime cash-flow telemetry aggregated for the Player Financial Dossier. */
  financialStats: PlayerFinancialStats;
  /** Rolling transaction history (newest first, capped) shown in the dossier audit log. */
  ledger: PlayerLedgerEntry[];
}

// ---- Player Financial Dossier: telemetry, credit rating & audit log ----

/** Aggregated lifetime inflows / outflows per player (dossier "Cash Flow" tab). */
export interface PlayerFinancialStats {
  /** Rent collected from rivals. */
  rentCollected: number;
  /** Rent paid to rivals. */
  rentPaid: number;
  /** Proceeds from selling shares. */
  shareSaleProceeds: number;
  /** Cash spent on share purchases. */
  sharePurchases: number;
  /** Cash spent on building upgrades. */
  buildingUpgrades: number;
  /** Realised yield / withdrawals from treasury funds. */
  investmentYield: number;
  /** Transit dividends collected. */
  transitDividends: number;
  /** Interest paid to the bank (loans + informal debt). */
  interestPaid: number;
  /** Insurance premiums paid. */
  insurancePremiums: number;
  /** Bank fees, penalties, taxes and distressed-refinancing charges. */
  bankFees: number;
  /** Value of shares seized by the bank in foreclosure. */
  foreclosureSeized: number;
  /** Loan principal drawn (cash in). */
  loanDraws: number;
}

/** One colour-coded line in the dossier audit log. */
export interface PlayerLedgerEntry {
  id: number;
  round: number;
  kind: 'INCOME' | 'EXPENSE';
  category: keyof PlayerFinancialStats;
  label: string;
  /** Positive magnitude of the movement. */
  amount: number;
}

/** Letter grade used by lenders; derived from `Player.creditScore`. */
export type CreditRating = 'A+' | 'B' | 'C' | 'D';

/** Rating metadata: display label, accent colour and the interest markup lenders charge. */
export const CREDIT_RATING_META: Record<CreditRating, { label: string; accent: string; blurb: string; markup: number; ceilingMultiplier: number }> = {
  'A+': { label: 'A+ Excelentní', accent: '#34d399', blurb: 'Prime borrower — best rates and full credit line.', markup: 0, ceilingMultiplier: 1 },
  B: { label: 'B Standardní', accent: '#fbbf24', blurb: 'Normal credit terms; the lower B band pays a +0.5 % premium.', markup: 0.005, ceilingMultiplier: 1 },
  C: { label: 'C Rizikový', accent: '#fb923c', blurb: 'Distressed — lenders charge a +2 % risk premium.', markup: 0.02, ceilingMultiplier: 0.8 },
  D: { label: 'D Distress', accent: '#f43f5e', blurb: 'Near default — restricted, expensive credit (+5 %).', markup: 0.05, ceilingMultiplier: 0.5 },
};

/** Credit score improvement per successful loan payment (principal + interest). */
export const CREDIT_SCORE_IMPROVEMENT_PER_PAYMENT = 2;
/** Extra credit-score improvement when a loan is repaid in full at maturity. */
export const CREDIT_SCORE_IMPROVEMENT_AT_MATURITY = 3;
/** Credit score penalty when refinancing a loan. */
export const REFINANCE_CREDIT_PENALTY = 15;
/** Credit score penalty when selling a property (refinancing via asset sale). */
export const ASSET_SALE_CREDIT_PENALTY = 10;

/** Neutral credit score for a fresh player (grade B). */
export const DEFAULT_CREDIT_SCORE = 70;
/** Flat rate markup applied to a restructured (distressed) loan. */
export const REFINANCE_RATE_PENALTY = 0.03;
/** Bank liquidation penalty added on top of the shortfall during foreclosure. */
export const FORECLOSURE_PENALTY = 0.2;

/**
 * Risk premium charged on top of the market base rate, banded by credit score.
 * A prime borrower (80+) pays the base rate; a near-default borrower pays a
 * punitive premium. Every successful payment moves the score up one band at a
 * time, so a disciplined player can earn cheaper credit again.
 */
export const creditRiskPremiumOf = (score: number): number => {
  if (score >= 85) return 0;
  if (score >= 70) return 0;
  if (score >= 60) return 0.005;
  if (score >= 40) return 0.02;
  if (score >= 25) return 0.035;
  return 0.05;
};

/** Map a 0–100 credit score onto the lender letter grade. */
export const creditRatingOf = (score: number): CreditRating => {
  if (score >= 85) return 'A+';
  if (score >= 60) return 'B';
  if (score >= 35) return 'C';
  return 'D';
};

/** Zeroed telemetry bucket for a new player. */
export const emptyFinancialStats = (): PlayerFinancialStats => ({
  rentCollected: 0,
  rentPaid: 0,
  shareSaleProceeds: 0,
  sharePurchases: 0,
  buildingUpgrades: 0,
  investmentYield: 0,
  transitDividends: 0,
  interestPaid: 0,
  insurancePremiums: 0,
  bankFees: 0,
  foreclosureSeized: 0,
  loanDraws: 0,
});

export interface MarketState {
  currentRound: number;
  /** 0 = endless / target mode (no hard round cap). */
  maxRounds: number;
  phase: MarketPhase;
  /** Round in which the current macro phase ends (randomised, 4–8 rounds). */
  phaseEndsRound?: number;
  rentMultiplier: number;
  assetValuationMultiplier: number;
  interestRate: number;
}

export interface ProposalState {
  propertyId: string;
  proposerId: string;
  targetUpgradeLevel: number;
  totalCost: number;
  newSharesIssued: number;
  votesFor: number;
  votesAgainst: number;
  votedPlayerIds: string[];
  status: 'PENDING' | 'PASSED' | 'REJECTED';
}

export interface BidRecord {
  bidderId: string;
  bidderName: string;
  amount: number;
  round: number;
}

export interface AuctionState {
  propertyId: string;
  shareTranche: number;
  /** When set, the tranche is sold by this player instead of the public float. */
  sellerId?: string | null;
  currentBid: number;
  highestBidderId: string | null;
  timeRemaining: number;
  activePlayerIds: string[];
  isOpen: boolean;
  bidHistory: BidRecord[];
  thinkingBidderId: string | null;
  /** Human's turn first: bots wait until the human bids or folds. */
  humanFolded: boolean;
  /** Hotseat turn-based bidding: seating order of all bidders + whose slot it is. */
  turnOrder: string[];
  turnIndex: number;
  /** Players who passed/folded this auction. */
  foldedPlayerIds: string[];
}

/**
 * Insolvency Resolution Desk: raised when a HUMAN player's loan matures and their
 * cash cannot cover the amount due. The game turn pauses — no silent auto-deduction,
 * no negative cash, no automatic liquidation — until the player covers the shortfall
 * (refinance / treasury withdrawal / controlled asset sale) or goes bankrupt.
 */
export interface InsolvencyDesk {
  playerId: string;
  playerName: string;
  /** The matured loan awaiting settlement (kept in `loans` until resolved). */
  loanId: string;
  /** Full amount falling due at maturity. */
  amountDue: number;
  /** amountDue − max(0, cash) at the moment the desk opened. */
  shortfall: number;
  /** Human-readable loan label (e.g. "Anuitní úvěr"). */
  label: string;
}

export interface EmergencySettlement {
  debtorId: string;
  debtorName: string;
  amountDue: number;
  cashAvailable: number;
  deficit: number;
  creditorShares: { playerId: string; playerName: string; amount: number }[];
  label: string;
  /** tile where the liability was incurred (swap priority) */
  tileId: string | null;
}

export interface OfferChoice {
  tileId: string;
  tranche: number;
}

export type GameStateTurnPhase =
  | 'TURN_START'
  | 'MOVEMENT_PREVIEW'
  | 'PUSH_LUCK_DECISION'
  | 'TILE_RESOLUTION'
  | 'FAST_TRAVEL_SELECTION'
  | 'CITY_HALL_LOBBY'
  | 'TAKEOVER_MODAL'
  | 'GOVERNANCE_PHASE'
  | 'EXPANSION_CHECK'
  | 'TURN_END'
  /** Hotseat pass-the-device interstitial: next up is a human waiting for the controls. */
  | 'HANDOVER'
  /** Hard-pause gate: the engine freezes until the human acknowledges the splash modal. */
  | 'ATTENTION_INTERCEPT'
  /** Bilateral dealmaking in flight: player trade builder or incoming offer review. */
  | 'TRADE_NEGOTIATION';

export type BlackSwanEvent = 'TECH_RALLY' | 'CREDIT_CRUNCH' | 'INFRASTRUCTURE_GRANT';

export interface BlackSwanState {
  event: BlackSwanEvent | null;
  title: string;
  description: string;
}

export interface TradeShareLot {
  tileId: string;
  shares: number;
}

/** One observed cash movement, used to drive the flying-cash animation and ledger glow. */
export interface MoneyTransfer {
  id: number;
  /** Payer id, or 'BANK' when money comes from / goes to the board. */
  fromId: string;
  toId: string;
  amount: number;
  /** Short human-readable label (usually the newest activity-log line). */
  reason: string;
  ts: number;
}

/** Machine-readable reason a bot accepted/rejected a trade (drives the UI speech bubble). */
export type TradeVerdictReason =
  | 'ACCEPT_SET' // incoming shares complete the bot's own colour set
  | 'ACCEPT_CASH' // bot is cash-starved and needs liquidity
  | 'ACCEPT_VALUE' // plain favourable value ratio
  | 'BLOCK_MONOPOLY' // would hand the sender a full colour monopoly
  | 'KEEP_MAJORITY' // would break the bot's own majority / blocking stake
  | 'BUFFER' // conservative cash-buffer floor
  | 'CARTEL_EMBARGO' // anti-leader cartel: hegemon embargo / 3.5× markup
  | 'BALANCE'; // simply not enough value

export interface TradeOffer {
  id: string;
  senderId: string;
  receiverId: string;
  offeredCash: number;
  offeredShares: TradeShareLot[];
  requestedCash: number;
  requestedShares: TradeShareLot[];
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED';
  /** Bot verdict (strategic reasoning) attached once the receiver is an AI. */
  verdictKind?: TradeVerdictReason;
  verdictNote?: string;
}

export type AttentionKind = 'AUCTION' | 'TRADE' | 'VOTE' | 'TAKEOVER';

/** Attention Intercept gate: freezes the loop until the human acknowledges. */
export interface AttentionState {
  kind: AttentionKind;
  title: string;
  body: string;
  /** AUCTION: staged auction payload */
  tileId?: string;
  tranche?: number;
  /** TAKEOVER: who is buying */
  buyerId?: string;
  offerId?: string;
  /** What is at stake — rendered as a labelled breakdown inside the splash. */
  stake?: { label: string; value: string }[];
  /** FSM phase to restore once the human acknowledges the gate. */
  resumePhase?: GameStateTurnPhase;
  /** TRADE: plain-language summary lines shown in the accept/decline splash. */
  tradeSummary?: { give: string[]; take: string[]; fromName: string; note: string };
}
/** Play-by-play broadcast phases for a bot turn (no silent skipping). */
export type BotBroadcastPhase = 'ROLL' | 'ROLLED' | 'MOVE' | 'SETTLE' | 'GOVERN';

export interface BotBroadcast {
  playerId: string;
  phase: BotBroadcastPhase;
  text: string;
}

/** Toast raised when cash/shares change hands after an accepted trade. */
export interface TradeToast {
  id: number;
  title: string;
  lines: string[];
}

export type GameEventKind = 'INFO' | 'GAIN' | 'LOSS' | 'RISK' | 'EXPANSION' | 'BANKRUPTCY';

export interface GameEvent {
  id: number;
  round: number;
  message: string;
  kind: GameEventKind;
}
